/**
 * LU5 Pseudocode Exercises — interactive widget embedded in Chapter 2 of
 * StudyGuideLU5Gr10. Five-tab framework with progress tracking and
 * localStorage persistence. Phase 1: only Exercise 1 (Sequence) is fully
 * implemented; Exercises 2-5 render placeholders.
 *
 * Design: matches the LU5 amber palette exactly. Self-contained — no
 * dependency on the parent file's helper components.
 */
import { useState, useEffect, ReactNode } from "react";
import { CheckCircle, XCircle, ChevronRight, Eye } from "lucide-react";

// ─── Tabs ─────────────────────────────────────────────────────────────────────

interface ExerciseTab {
  id: number;
  short: string;
}

const EXERCISES: ExerciseTab[] = [
  { id: 1, short: "Sequence" },
  { id: 2, short: "Selection" },
  { id: 3, short: "Spot the error" },
  { id: 4, short: "Fill in the blanks" },
  { id: 5, short: "Write it yourself" },
];

const STORAGE_KEY = "lu5-pseudocode-exercises-completed-v1";

// ─── localStorage hook ────────────────────────────────────────────────────────

function useLocalStorageState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? (JSON.parse(stored) as T) : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore quota or disabled storage */
    }
  }, [key, value]);

  return [value, setValue];
}

// ─── Mini helpers (self-contained styling) ────────────────────────────────────

function PseudoCode({ children }: { children: string }) {
  return (
    <pre className="bg-amber-950/90 text-amber-50 rounded-md p-3 text-sm font-mono whitespace-pre overflow-x-auto leading-relaxed">
      {children}
    </pre>
  );
}

function ScenarioBox({ children }: { children: ReactNode }) {
  return (
    <div className="border-l-4 border-amber-700 bg-amber-100/60 px-4 py-3 my-4 text-amber-950 text-sm">
      {children}
    </div>
  );
}

function FeedbackBox({ ok, children }: { ok: boolean; children: ReactNode }) {
  const cls = ok
    ? "border-emerald-600 bg-emerald-50 text-emerald-900"
    : "border-rose-600 bg-rose-50 text-rose-900";
  const Icon = ok ? CheckCircle : XCircle;
  return (
    <div className={`flex items-start gap-2 border-l-4 ${cls} px-4 py-3 my-3 text-sm`}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-200 text-amber-900 border border-amber-300">
      {children}
    </span>
  );
}

// ─── Exercise 1 — Sequence ────────────────────────────────────────────────────

const E1_OPTIONS = [
  {
    letter: "A",
    code: `BEGIN
  INPUT price
  INPUT quantity
  SET total = price * quantity
  DISPLAY total
END`,
  },
  {
    letter: "B",
    code: `BEGIN
  SET total = price * quantity
  INPUT price
  INPUT quantity
  DISPLAY total
END`,
  },
  {
    letter: "C",
    code: `BEGIN
  DISPLAY "Enter price:"
  DISPLAY "Enter quantity:"
  SET total = price * quantity
  DISPLAY total
END`,
  },
];

const E1_CORRECT = "A";

const E1_FEEDBACK: Record<string, { ok: boolean; msg: string }> = {
  A: {
    ok: true,
    msg:
      "Correct. The values have to be input before the calculation can use them. Sequence — the order of the steps — is what makes this work.",
  },
  B: {
    ok: false,
    msg:
      "Not quite. Look at line 2: SET total = price * quantity runs before price and quantity have been input. There are no values to multiply yet, so total would be undefined (or zero, depending on the language).",
  },
  C: {
    ok: false,
    msg:
      "Not quite. Those DISPLAY lines just print prompt text on the screen — they don't read any input. You need INPUT statements to actually capture the user's answer into a variable.",
  },
};

const E1_PARTB_MODEL = `BEGIN
  DISPLAY "What is your name?"
  INPUT name
  DISPLAY "What is your age?"
  INPUT age
  DISPLAY "Hello, " + name + "! You are " + age + " years old."
END`;

interface ExerciseProps {
  done: boolean;
  onComplete: () => void;
}

function Exercise1Sequence({ done, onComplete }: ExerciseProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [partBText, setPartBText] = useState("");
  const [partBRevealed, setPartBRevealed] = useState(false);

  const partACorrect = submitted && selected === E1_CORRECT;

  const handleCheck = () => {
    if (!selected) return;
    setSubmitted(true);
    if (selected === E1_CORRECT && partBRevealed && !done) {
      onComplete();
    }
  };

  const handleReveal = () => {
    setPartBRevealed(true);
    if (partACorrect && !done) {
      onComplete();
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-xl font-bold text-amber-950" style={{ fontFamily: "Palatino, serif" }}>
          Exercise 1 — Sequence
        </h3>
        <Tag>Sequence</Tag>
      </div>

      <ScenarioBox>
        A tuck shop wants a program that asks a customer for the price of one item and how many they
        want, then calculates and displays the total cost.
      </ScenarioBox>

      <p className="font-semibold text-amber-950 mt-4 mb-2">
        Part A — Which pseudocode correctly solves this? Choose one.
      </p>

      <div className="space-y-3">
        {E1_OPTIONS.map((opt) => {
          const isSelected = selected === opt.letter;
          const showFeedback = submitted && isSelected;
          const fb = E1_FEEDBACK[opt.letter];
          const ringClass = showFeedback
            ? fb.ok
              ? "ring-2 ring-emerald-500"
              : "ring-2 ring-rose-500"
            : isSelected
            ? "ring-2 ring-amber-500"
            : "ring-1 ring-amber-300/40 hover:ring-amber-500";
          return (
            <label
              key={opt.letter}
              className={`block cursor-pointer rounded-lg p-3 transition ${ringClass}`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="e1-partA"
                  className="mt-1.5 accent-amber-700"
                  checked={isSelected}
                  onChange={() => {
                    setSelected(opt.letter);
                    setSubmitted(false);
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-amber-900 mb-1">{opt.letter}</div>
                  <PseudoCode>{opt.code}</PseudoCode>
                </div>
              </div>
            </label>
          );
        })}
      </div>

      <button
        onClick={handleCheck}
        disabled={!selected}
        className="mt-4 px-4 py-2 rounded-md bg-amber-800 text-amber-50 text-sm font-semibold hover:bg-amber-900 transition disabled:bg-amber-300 disabled:cursor-not-allowed"
      >
        Check answer
      </button>

      {submitted && selected && (
        <FeedbackBox ok={E1_FEEDBACK[selected].ok}>{E1_FEEDBACK[selected].msg}</FeedbackBox>
      )}

      {/* Part B */}
      <div className="mt-8 pt-6 border-t border-amber-200">
        <p className="font-semibold text-amber-950 mb-2">Part B — Now write your own.</p>
        <p className="text-sm text-amber-900 mb-3">
          Write pseudocode that asks the user for their name and age, then displays a friendly
          greeting like <em>"Hello, [name]! You are [age] years old."</em> Use{" "}
          <code className="bg-amber-100 px-1 rounded">DISPLAY</code> to prompt the user before each{" "}
          <code className="bg-amber-100 px-1 rounded">INPUT</code>.
        </p>

        <textarea
          value={partBText}
          onChange={(e) => setPartBText(e.target.value)}
          rows={8}
          spellCheck={false}
          placeholder={`BEGIN\n  DISPLAY "..."\n  INPUT ...\n  ...\nEND`}
          className="w-full rounded-md bg-amber-50 border border-amber-300 px-3 py-2 text-sm font-mono text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />

        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={handleReveal}
            className="px-4 py-2 rounded-md bg-amber-100 text-amber-900 text-sm font-semibold hover:bg-amber-200 border border-amber-300 transition flex items-center gap-1.5"
          >
            <Eye className="w-4 h-4" />
            {partBRevealed ? "Hide model answer" : "Show model answer"}
          </button>
          {!partBRevealed && (
            <span className="text-xs text-amber-800 italic">Try it first, then compare.</span>
          )}
        </div>

        {partBRevealed && (
          <div className="mt-3">
            <p className="text-xs text-amber-800 mb-1.5 italic">Model answer (one valid version):</p>
            <PseudoCode>{E1_PARTB_MODEL}</PseudoCode>
            <p className="text-xs text-amber-700 mt-2">
              Other answers are valid too — for example, asking for both name and age inside one
              prompt, or using <code className="bg-amber-100 px-1 rounded">+</code> vs string
              concatenation differently. The key thing is the <em>order</em>: prompt → input →
              prompt → input → output.
            </p>
          </div>
        )}
      </div>

      {done && (
        <div className="mt-6 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
          <CheckCircle className="w-4 h-4" />
          Exercise complete.
        </div>
      )}
    </div>
  );
}

// ─── Placeholder for exercises 2–5 ────────────────────────────────────────────

function ExercisePlaceholder({ id, short }: { id: number; short: string }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-xl font-bold text-amber-950" style={{ fontFamily: "Palatino, serif" }}>
          Exercise {id} — {short}
        </h3>
      </div>
      <div className="border-l-4 border-amber-300 bg-amber-50 px-4 py-3 my-3 text-amber-900 text-sm flex items-start gap-2">
        <ChevronRight className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <strong>Coming soon.</strong> This exercise is in design — Exercise 1 (Sequence) is the
          live one to try right now. Once Exercise 1 is solid we'll roll out the rest in the same
          shape.
        </div>
      </div>
    </div>
  );
}

// ─── Main widget ──────────────────────────────────────────────────────────────

export default function LU5PseudocodeExercises() {
  const [activeId, setActiveId] = useState(1);
  const [completed, setCompleted] = useLocalStorageState<Record<string, boolean>>(STORAGE_KEY, {});

  const completedCount = Object.values(completed).filter(Boolean).length;

  const markComplete = (id: number) => {
    setCompleted({ ...completed, [String(id)]: true });
  };

  return (
    <div className="my-6 rounded-2xl border border-amber-900/20 bg-gradient-to-br from-amber-50 to-amber-100/40 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-amber-200 bg-amber-100/40">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h3
            className="text-lg font-bold text-amber-950"
            style={{ fontFamily: "Palatino, serif" }}
          >
            Try it yourself — Pseudocode exercises
          </h3>
          <div className="text-sm text-amber-900">
            <strong>{completedCount}</strong> of {EXERCISES.length} completed
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {EXERCISES.map((ex) => {
            const isActive = activeId === ex.id;
            const isDone = !!completed[String(ex.id)];
            return (
              <button
                key={ex.id}
                onClick={() => setActiveId(ex.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition flex items-center gap-1.5 ${
                  isActive
                    ? "bg-amber-800 text-amber-50"
                    : "bg-white text-amber-900 border border-amber-300 hover:bg-amber-50"
                }`}
              >
                <span>{ex.id} · {ex.short}</span>
                {isDone && (
                  <CheckCircle
                    className={`w-3.5 h-3.5 ${isActive ? "text-emerald-300" : "text-emerald-600"}`}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5">
        {activeId === 1 && (
          <Exercise1Sequence
            done={!!completed["1"]}
            onComplete={() => markComplete(1)}
          />
        )}
        {activeId !== 1 && (
          <ExercisePlaceholder
            id={activeId}
            short={EXERCISES.find((e) => e.id === activeId)?.short ?? ""}
          />
        )}
      </div>
    </div>
  );
}
